import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, getAuthToken } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type { Match } from "@/lib/types";

/**
 * CoachMatchUploadModal — coach-facing upload form for a match slot (M12b).
 *
 * Single-shot upload via `POST /api/matches/{id}/upload-video?slot=…`. The
 * backend's chunked-session API (`/api/matches/{id}/upload-video/session`
 * + the `/api/uploads/sessions/{id}/...` lifecycle) exists and is reachable
 * by coaches as of M12b's authz changes, but the chunked client is a
 * larger build — deferred to a follow-up so this milestone can land the
 * happy path. The single-shot endpoint accepts the same .mp4 / .mkv
 * extensions, validates the team via the same `_require_match_upload_actor`
 * helper, and kicks off the same transcode pipeline on success.
 *
 * On success the modal invalidates every matches cache (so the row's
 * "ready / transcoding / error" counts update without a manual refresh),
 * then closes.
 */
export function CoachMatchUploadModal({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [slot, setSlot] = useState<"full" | "first_half" | "second_half">(
    "full",
  );
  const [file, setFile] = useState<File | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    inputRef.current?.focus();
    return () => {
      mountedRef.current = false;
      // Strip the XHR's event handlers BEFORE abort() so the error/abort
      // callbacks don't fire setState on an unmounted component. The XHR
      // itself is cancelled either way; we just don't want React 18's
      // "setState on unmounted" warning chain.
      const xhr = xhrRef.current;
      if (xhr) {
        xhr.onload = null;
        xhr.onerror = null;
        xhr.onabort = null;
        xhr.upload.onprogress = null;
        xhr.abort();
      }
      previousFocusRef.current?.focus();
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && !pending) {
      event.preventDefault();
      onClose();
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("Pick a video file (.mp4 or .mkv) first.");
      return;
    }
    setError(null);
    setPercent(0);
    setPending(true);
    try {
      // `fetch` doesn't expose upload progress, so use XHR for the
      // streaming `progress` event. Single-shot endpoint matches the
      // backend's `POST /api/matches/{id}/upload-video?slot=…` route.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open(
          "POST",
          `/api/matches/${encodeURIComponent(match.id)}/upload-video?slot=${slot}`,
        );
        const token = getAuthToken();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setPercent(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else {
            let detail: string | null = null;
            try {
              const body = JSON.parse(xhr.responseText) as {
                detail?: unknown;
              };
              if (typeof body.detail === "string") detail = body.detail;
            } catch {
              /* fall through to status */
            }
            reject(new ApiError(xhr.status, detail ?? `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new ApiError(0, "Network error"));
        xhr.onabort = () => reject(new ApiError(0, "Upload cancelled"));
        const fd = new FormData();
        fd.append("file", file);
        // TODO(post-M12): swap to the chunked upload-session API
        // (`POST /api/matches/{id}/upload-video/session` + the
        // `/api/uploads/sessions/{id}/{chunk,complete}` lifecycle) for
        // resumable large-file uploads. Single-shot is the M12b MVP.
        xhr.send(fd);
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("public-matches"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-matches"),
        }),
        queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
        queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
      ]);
      onClose();
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string" ? err.detail : "Upload failed.",
        );
      } else {
        setError("Upload failed.");
      }
    } finally {
      xhrRef.current = null;
      if (mountedRef.current) setPending(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Upload match video"
      data-testid="coach-upload-modal"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 p-4"
    >
      <form
        onSubmit={onSubmit}
        className="flex w-full max-w-md flex-col gap-4 rounded-xl border border-rule bg-surface p-5 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-display uppercase tracking-wider text-ink">
              Upload match video
            </h3>
            <p className="mt-1 text-xs text-ink-muted truncate">
              {match.home_team || "—"} vs {match.away_team || "—"}
              {match.date ? ` · ${match.date}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            data-testid="coach-upload-modal-close"
            className="rounded border border-rule px-2 py-1 text-xs text-ink-muted hover:text-ink"
          >
            Close
          </button>
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">Slot</span>
          <select
            value={slot}
            onChange={(e) =>
              setSlot(e.target.value as "full" | "first_half" | "second_half")
            }
            disabled={pending}
            data-testid="coach-upload-modal-slot"
            className="tktic-select w-full"
          >
            <option value="full">Full match</option>
            <option value="first_half">First half</option>
            <option value="second_half">Second half</option>
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Video file (.mp4 or .mkv)
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".mp4,.mkv,video/mp4,video/x-matroska"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={pending}
            data-testid="coach-upload-modal-file"
            className="text-xs text-ink"
          />
        </label>

        {percent !== null ? (
          <div data-testid="coach-upload-modal-progress" className="flex flex-col gap-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-bg-card">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="font-mono text-[0.65rem] text-ink-muted">
              {percent}%
            </span>
          </div>
        ) : null}

        {error ? (
          <p
            role="alert"
            data-testid="coach-upload-modal-error"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-rule pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded border border-rule bg-bg-card px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink hover:bg-surface"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending || !file}
            data-testid="coach-upload-modal-submit"
            className="rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-white hover:opacity-90 disabled:opacity-60"
          >
            {pending ? "Uploading…" : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
}
